import { useRouter } from 'next/router';
import { parseCookies } from 'nookies';
import { useEffect, useMemo } from 'react';
import { isAndroid, isIOS } from 'react-device-detect';

import { compose, equals, ifElse, path, replace } from 'f';

import { getMixpanelDistinctId } from 'lib/analytics/mixpanel-helpers';
import { getCurrentUserId } from 'lib/auth';
import { useIsBreakpointWidth } from 'lib/hooks/useIsBreakpointWidth';
import * as Select from 'lib/select';

import { useTalkPageContext } from 'components/pages/dubbing/slug/talk-page-context';
import useVideoPlayerStore from '../../store';

export const useSetup = video => {
  const router = useRouter();
  const { asPath, autoplay, muted } = useTalkPageContext();
  const { onService, onTalk, ready, onSeek, autoplayed, onCurrentTime } =
    useVideoPlayerStore(state => ({
      autoplayed: state.autoplayed,
      onService: state.onService,
      onTalk: state.onTalk,
      ready: state.ready,
      onSeek: state.onSeek,
      onCurrentTime: state.onCurrentTime
    }));

  const shouldAutoplay = autoplay || autoplayed;
  const mixpanelDistinctId = getMixpanelDistinctId();
  const userId = parseCookies()._ted_user_id || getCurrentUserId();
  const isMobileDevice = isAndroid || isIOS;
  const isMobileWeb = useIsBreakpointWidth({
    size: 'sm',
    breakPointType: 'tui'
  });
  const isMobile = isMobileDevice || isMobileWeb;

  const { playerData: acmePlayerData } = useMemo(() => video, [video]);
  const playerTalkData = useMemo(() => {
    const hasPlayerData = Boolean(acmePlayerData);

    if (!hasPlayerData) {
      return false;
    }

    const playerDataObject = JSON.parse(String(acmePlayerData));

    if (router.query.preview == null) {
      return playerDataObject;
    }

    const streamUrl = new URL(playerDataObject.resources.hls.stream);
    streamUrl.searchParams.set('preview', 'true');
    playerDataObject.resources.hls.stream = streamUrl.toString();

    if (playerDataObject.resources.hls.metadata) {
      const metadataUrl = new URL(playerDataObject.resources.hls.metadata);
      metadataUrl.searchParams.set('preview', 'true');
      playerDataObject.resources.hls.metadata = metadataUrl.toString();
    }

    return playerDataObject;
  }, [acmePlayerData, router.query.preview]);
  const service = Select.service(playerTalkData);
  const youtubeID = path(['external', 'code'])(playerTalkData);

  const anchor = useMemo(
    () =>
      compose(
        (arr: string[]) => arr[arr.length - 1] || '',
        (str: string) => str.split('#'),
        (str: string) => str || ''
      )(asPath),
    [asPath]
  );

  const startTime = ifElse(
    compose(equals(0), (x: string) => x.indexOf('t-')),
    compose((x: number): number => x / 1000, Number, replace('t-', '')),
    () => 0
  )(anchor);

  useEffect(() => {
    // Determine if internal or youtube player is being used and set the service
    onService(video);
    onTalk(video);
    onCurrentTime(0);
  }, [onCurrentTime, onService, onTalk, video]);

  useEffect(() => {
    if (startTime && ready) {
      // If the player is ready and the start time is set, seek to the start time
      onSeek(startTime);
    }
  }, [ready, startTime, onSeek]);

  return {
    isMobile,
    shouldAutoplay,
    mixpanelDistinctId,
    userId,
    playerTalkData,
    service,
    youtubeID,
    muted,
    startTime
  };
};
